import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { IMAGES, BRAND } from './data';
import { Marquee } from './Marquee';

// Colors for the 5 tag chips at the bottom of the photo column
const TAG_STYLES = [
  { bg: '#D0FF3F', color: '#0B0B0B' }, // lime
  { bg: '#F5C846', color: '#0B0B0B' }, // amber
  { bg: '#FF553B', color: '#0B0B0B' }, // coral
  { bg: '#4CDCE6', color: '#0B0B0B' }, // cyan
  { bg: '#F1E8D0', color: '#0B0B0B' }, // cream w/ border
];

export const About = () => {
  const { t } = useTranslation();
  const paragraphs = t('about.paragraphs', { returnObjects: true }) || [];
  const glance = t('about.glance', { returnObjects: true }) || [];
  const tags = t('about.tags', { returnObjects: true }) || [];

  return (
    <>
      <Marquee items={t('about.marqueeItems', { returnObjects: true }) || []} bg="#4CDCE6" />

      <section id="about" className="relative border-b border-ink py-16 md:py-24 overflow-hidden" data-testid="about">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          {/* Section eyebrow */}
          <div className="flex items-center gap-3 mb-8 md:mb-10">
            <span className="font-mono text-xs bg-ink text-page px-2 py-1 tracking-widest2">§ 04</span>
            <span className="font-mono uppercase text-xs tracking-widest2 text-ink">{t('about.eyebrow')}</span>
          </div>

          {/* TOP: massive headline + top-right body */}
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
            <div className="col-span-12 lg:col-span-8">
              <h2 className="display leading-[0.9] text-ink">
                <span className="block text-[13vw] md:text-[10vw] lg:text-[7.5rem]">{t('about.h1_pre')}</span>
                <span className="serif-italic block text-[13vw] md:text-[10vw] lg:text-[7.5rem] leading-none mt-1">
                  {t('about.h1_italic')} —
                </span>
                <span className="block text-[13vw] md:text-[10vw] lg:text-[7.5rem] mt-2">{t('about.h1_founder')}</span>
                <span className="block leading-none mt-2 md:mt-3">
                  <span
                    className="inline-block px-2 md:px-3 text-ink text-[13vw] md:text-[10vw] lg:text-[7.5rem]"
                    style={{ background: '#D0FF3F' }}
                  >
                    {t('about.h1_hl')}
                  </span>
                </span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-4 lg:pt-56 xl:pt-64">
              <p className="serif-italic text-base md:text-lg leading-snug text-ink/85 max-w-sm lg:ml-auto">
                {t('about.tagline')}
              </p>
            </div>
          </div>

          {/* BOTTOM: photo + at-a-glance card / intro + body */}
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* LEFT: photo frame + at-a-glance + chips */}
            <div className="col-span-12 lg:col-span-5 flex flex-col gap-5 md:gap-6">
              {/* Photo frame */}
              <div className="relative">
                {/* Yellow post-it "keep the marketing moving." floats over top-right of photo */}
                <motion.div
                  initial={{ opacity: 0, rotate: -4, y: 10 }}
                  whileInView={{ opacity: 1, rotate: 5, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.4 }}
                  className="hidden md:block absolute -top-4 -right-6 z-10 px-4 py-3 shadow-[4px_5px_0_#0B0B0B] max-w-[190px]"
                  style={{ background: '#F5C846', border: '1px solid #0B0B0B' }}
                >
                  <p className="serif-italic text-sm leading-tight text-ink">
                    &ldquo;{t('about.postit')}&rdquo;
                  </p>
                </motion.div>

                <div className="box-line bg-page p-3 md:p-4 relative">
                  {/* Top-left dark tag */}
                  <div className="absolute top-6 left-6 md:top-7 md:left-7 z-10 bg-ink text-page px-2 py-1 font-mono uppercase text-[10px] tracking-widest2">
                    {t('about.photoTagTop')}
                  </div>
                  {/* Bottom-right dark tag */}
                  <div className="absolute bottom-6 right-6 md:bottom-7 md:right-7 z-10 bg-ink text-page px-2 py-1 font-mono uppercase text-[10px] tracking-widest2">
                    {t('about.photoTagBottom')}
                  </div>
                  <div className="relative aspect-[4/5] overflow-hidden border border-ink">
                    <img
                      src={IMAGES.portrait}
                      alt={`${BRAND.founder}, ${BRAND.name}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                {/* Photo meta caption */}
                <div className="mt-3 flex items-center justify-between font-mono uppercase text-[10px] tracking-widest2 text-ink/70">
                  <span>{t('about.photoMetaLeft')}</span>
                  <span>{t('about.photoMetaRight')}</span>
                </div>
              </div>

              {/* AT A GLANCE dark card */}
              <div className="box-line bg-ink text-page p-5 md:p-6">
                <p className="font-mono uppercase text-[11px] tracking-widest2 text-lime mb-4">— {t('about.glanceLabel')}</p>
                <div className="grid grid-cols-2 gap-4">
                  {glance.map((g, i) => (
                    <div key={i} className="border border-page/25 p-3">
                      <p className="font-mono uppercase text-[10px] tracking-widest2 text-lime mb-1.5">{g.label}</p>
                      <p className="font-mono uppercase text-sm text-page leading-snug">{g.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Colored badge tags */}
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, i) => {
                  const style = TAG_STYLES[i % TAG_STYLES.length];
                  return (
                    <span
                      key={i}
                      className="border border-ink px-3 py-1.5 font-mono uppercase text-xs tracking-widest text-ink"
                      style={{ background: style.bg }}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: intro copy + buttons */}
            <div className="col-span-12 lg:col-span-7 lg:pl-4">
              <p className="font-mono uppercase text-[11px] tracking-widest2 text-ink/70 mb-5">
                — {t('about.intro_kicker')}
              </p>
              <h3 className="display leading-[0.95] text-ink text-4xl md:text-6xl lg:text-7xl">
                <span className="block">{t('about.intro_line1')}</span>
                <span className="block mt-2">
                  <span
                    className="inline-block px-2 md:px-3 text-ink"
                    style={{ background: '#4CDCE6' }}
                  >
                    {t('about.intro_hl')}
                  </span>
                </span>
                <span className="block mt-2">
                  {t('about.intro_behind')}{' '}
                  <span className="serif-italic normal-case text-4xl md:text-6xl lg:text-7xl">
                    {t('about.intro_italic')}
                  </span>
                </span>
              </h3>

              <div className="mt-8 md:mt-10 space-y-5 max-w-2xl">
                {paragraphs.map((p, i) => {
                  // Second-to-last paragraph rendered as a highlighted quote box
                  const isQuote = p.startsWith('__QUOTE__');
                  const text = p.replace('__QUOTE__', '').trim();
                  if (isQuote) {
                    return (
                      <div key={i} className="box-line px-5 py-4">
                        <p className="serif-italic text-base md:text-lg leading-snug text-ink/90">{text}</p>
                      </div>
                    );
                  }
                  return (
                    <p key={i} className="serif-italic text-base md:text-lg leading-snug text-ink/85">{text}</p>
                  );
                })}
              </div>

              <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 bg-ink text-page px-6 py-3.5 font-mono uppercase tracking-widest text-xs border border-ink hover:bg-lime hover:text-ink transition-colors duration-300"
                  data-testid="about-cta-work"
                >
                  {t('about.ctaWork')}
                  <span aria-hidden>→</span>
                </a>
                <a
                  href="#services"
                  className="inline-flex items-center gap-2 bg-lime text-ink px-6 py-3.5 font-mono uppercase tracking-widest text-xs border border-ink hover:bg-ink hover:text-lime transition-colors duration-300"
                  data-testid="about-cta-see"
                >
                  {t('about.ctaSee')}
                  <span aria-hidden>→</span>
                </a>
              </div>

              <div className="mt-8 md:mt-10 flex items-end justify-between border-t border-ink/30 pt-4">
                <p className="serif-italic text-xl md:text-2xl text-ink">— {BRAND.founder.split(' ')[0]}</p>
                <p className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70">
                  {t('about.footerCredit')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
